import { useMemo, useEffect, useState } from 'react';
import { useInterval } from 'react-use';
import { post, put, get, del } from '../utils/api';
import { useStore, store, TOKEN, GUEST } from '../store';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import i18n from '../i18n';

import { useErrorSnack, useSuccessSnack } from '../utils/snackbar';
import { track } from '../utils/track';
import { parseJwt } from '../utils/jwt';

export async function login(email, password) {
  const res = await post(
    '/user/login',
    { email, password },
    { includeToken: false }
  );
  return res?.data;
}

export function logout() {
  track('logout');
  store[TOKEN].next(null);
}

export async function guestLogin() {
  const res = await post(
    '/guest/user/guest-login',
    {},
    { includeToken: false }
  );
  track('guest_login');
  return res?.data;
}

export function forgotPassword(email) {
  track('reset_password');
  return post('/user/forgot-password', { email });
}

export function resetPassword({ token, password }) {
  track('reset_password');
  return post('/user/reset-password', { token, password });
}

export async function getSelf() {
  if (!store[TOKEN].value) {
    return {};
  }
  const { role, secure_login } = parseJwt(store[TOKEN].value) || {};
  if (secure_login) {
    return {};
  }
  const isGuest = !role || role === 'guest';
  const res = await get(`${isGuest ? '/guest' : ''}/user/self`);

  return res?.data;
}

export async function createBankIDSetupToken() {
  const res = await post('/user/create-bank-id-setup-token');
  return res?.data?.token;
}
export async function createBankIDSetupTokenSecureLogin(token) {
  const res = await post(`/secure-login/bankid`, { token });
  return res?.data?.token;
}

export async function signup(data) {
  const res = await post('/user', data);
  return res?.data;
}

export async function updateUserEditStatus({ id, status }) {
  const res = await put(`/user/automatic-edits`, { id, status });
  return res?.data;
}

export async function updateUser({
  email,
  password,
  oldPassword,
  rebrandMessage,
  acceptedTosAt,
  meta,
  calendarOrgId,
  defaultLanguage,
  calendarFolder,
  language,
  phone,
  calendarSyncJoinBehavior,
  meetingBotSpeakers,
  defaultTemplate,
  defaultModel,
  emailNotifications,
  editSensitivity,
  country,
}) {
  track('update_user');
  const res = await put('/user', {
    email,
    password,
    oldPassword,
    rebrand_message: rebrandMessage,
    accepted_tos_at: acceptedTosAt,
    meta,
    calendar_org_id: calendarOrgId,
    default_language: defaultLanguage,
    calendar_folder: calendarFolder,
    language,
    phone,
    calendarSyncJoinBehavior,
    meeting_bot_speakers: meetingBotSpeakers,
    default_template: defaultTemplate,
    default_model: defaultModel,
    email_notifications: emailNotifications,
    edit_sensitivity: editSensitivity,
    country,
  });
  return res?.data;
}

async function switchWorkspace(workspaceId) {
  track('switch_workspace');
  const res = await post(`/organization/${workspaceId}/switch`);
  const token = res?.data?.token;
  store[TOKEN].next(token);
  return token;
}

async function sendVerificationEmail() {
  const res = await post('/user/send-verification-email');
  return res?.data;
}

async function verifyEmail(token) {
  const res = await post('/user/verify-email', { token });
  return res?.data;
}

async function sendVerificationPhone() {
  const res = await post('/user/send-verification-phone');
  return res?.data;
}

async function verifyPhone(code) {
  console.log('HERE', code);
  const res = await post('/user/verify-phone', { code });
  return res?.data;
}

export async function saveOnboarding(onboarding) {
  track('set_onboarding');
  const res = await put(`/user/onboarding`, { onboarding });
  return res?.data;
}

async function createUser({ email, password, plan, language }) {
  track('create_user', { plan });
  const res = await post('/admin/user', {
    email,
    password,
    plan,
    language,
  });
  return res?.data;
}

async function signInWithGoogle({ token, inviteToken, referralToken }) {
  track('sign_in_with_google');
  const res = await post('/login/google-redirect', {
    token,
    language: i18n.language,
    invite_token: inviteToken,
    referral_token: referralToken,
  });
  return res?.data;
}

async function getUsers({ search, inOrg } = {}) {
  const res = await get('/users', { params: { search, in_org: +!!inOrg } });
  return res?.data?.users;
}

async function getUser(userId) {
  const res = await get(`/user/${userId}`);
  return res?.data;
}

async function getUserBilling(userId) {
  const res = await get(`/user/${userId}/billing`);
  return res?.data?.events;
}

async function createNameList(userId, name) {
  const res = await post(`/user/${userId}/name-list`, { name });
  return res?.data;
}

async function createTranscribeMode(userId, name) {
  const res = await post(`/user/${userId}/transcribe-mode`, { name });

  return res?.data?.transcribeMode;
}

async function updateUserAdmin(
  userId,
  { plan, speakerIdentification, languages, autoShip, role, nin }
) {
  //updates arbitrary user. Requires that logged in user is admin
  const res = await put(`/user/${userId}`, {
    plan,
    speaker_identification: speakerIdentification,
    languages,
    auto_ship: autoShip,
    role,
    nin,
  });
  return res?.data;
}

async function deleteUserAdmin(userId) {
  //deletes arbitrary user. Requires that logged in user is admin
  const res = await del(`/user/${userId}`);
  return res?.data;
}

async function deleteSelf() {
  const res = await del('/user');
  store[TOKEN].next(null);
  return res?.data;
}

async function createOtp() {
  const res = await post('/user/otp');
  return res?.data;
}

async function verifyOtp({ token, secret, backupCodes }) {
  const res = await post('/user/otp/verify', {
    token,
    secret,
    backup_codes: backupCodes,
  });
  return res?.data;
}

async function deleteOtp({ token }) {
  const res = await del('/user/otp', { token });
  return res?.data;
}

export async function loginOtp(token) {
  const res = await post('/login/otp', { token });
  return res?.data;
}

async function createIntegration({ type, code, scopes }) {
  if (type === 'google') {
    const res = await post('/integrations/google', {
      refreshCode: code,
      scopes,
    });
    return res?.data;
  }
}

async function deleteIntegration({ id }) {
  const res = await del(`/user/integration/${id}`);
  return res?.data;
}

async function createWorkspace({ name, addUser = false, users, type } = {}) {
  const res = await post('/organization', {
    name,
    add_user: addUser,
    users,
    type,
  });
  return res?.data;
}

async function updateUserExportSettings({ userId, settings }) {
  const res = await put(`/user/${userId}/export-settings`, settings);
  return res?.data;
}

async function removeCalendarConnection() {
  const res = await del(`/user/calendar-sync`);
  return res?.data;
}

async function toggleCalendarSyncMeeting({ calendarId }) {
  const res = await post(`/user/calendar-sync/toggle-meeting`, {
    calendarId,
  });
  return res?.data;
}

async function toggleCalendarMeetingBotSpeakers({ calendarId }) {
  const res = await post(`/user/calendar-sync/toggle-meeting-bot-speakers`, {
    calendarId,
  });
  return res?.data;
}

export function useUserActions() {
  const client = useQueryClient();

  const {
    mutate: update,
    mutateAsync: updateUserAsync,
    isSuccess,
    isError,
    isLoading,
  } = useMutation(updateUser, {
    onSettled: () => {
      client.invalidateQueries(); //invalidates whole cache
      getSelf();
    },
  });

  useSuccessSnack(i18n.t('Information updaterad'), isSuccess);
  useErrorSnack(i18n.t('Fel uppstod vid updatering av användare'), isError);

  const { mutate: _switchWorkspace, mutateAsync: switchWorkspaceAsync } =
    useMutation(switchWorkspace, {
      onSettled: () => {
        client.invalidateQueries(); //invalidates whole cache
      },
    });

  const { mutate: _saveOnboarding, mutateAsync: _saveOnboardingAsync } =
    useMutation(saveOnboarding, {
      onSettled: () => {
        client.setQueryData('self', (old) => ({
          ...old,
          onboarding: { ...old?.onboarding, completed: true, skip: false },
        }));
        client.invalidateQueries('self'); //invalidates self cache
      },
    });

  const {
    mutate: _createOtp,
    mutateAsync: createOtpAsync,
    isSuccess: isOtpSuccess,
    isError: isOtpError,
    loading: isOtpLoading,
    data: otp,
  } = useMutation(createOtp);

  const {
    mutate: _verifyOtp,
    mutateAsync: verifyOtpAsync,
    isSuccess: isVerifyOtpSuccess,
    isError: isVerifyOtpError,
    error: verifyOtpError,
    reset: resetVerifyOtp,
  } = useMutation(verifyOtp, {
    onSettled: () => {
      client.invalidateQueries('self');
    },
  });

  const {
    mutate: _deleteOtp,
    mutateAsync: deleteOtpAsync,
    error: deleteOtpError,
    reset: resetDelete,
  } = useMutation(deleteOtp, {
    onSettled: () => {
      client.invalidateQueries('self');
    },
  });

  const {
    mutate: _sendVerificationEmail,
    mutateAsync: sendVerificationEmailAsync,
    isSuccess: isSendVerificationEmailSuccess,
    error: isSendVerificationEmailError,
    isLoading: isSendVerificationEmailLoading,
    reset: resetSendVerificationEmail,
  } = useMutation(sendVerificationEmail, {
    onSettled: () => {
      client.invalidateQueries('self');
    },
  });

  useErrorSnack(
    i18n.t('Kunde inte skicka verifieringsmail'),
    isSendVerificationEmailError
  );
  useSuccessSnack(
    i18n.t('Verifieringsmail skickat'),
    isSendVerificationEmailSuccess
  );

  const {
    mutate: _verifyEmail,
    mutateAsync: verifyEmailAsync,
    isSuccess: isVerifyEmailSuccess,
    error: isVerifyEmailError,
    isLoading: isVerifyEmailLoading,
    reset: resetVerifyEmail,
  } = useMutation(verifyEmail, {
    onSettled: () => {
      const selfData = client.getQueryData('self');
      if (!selfData) {
        // semi good way to check if the request is in flight
        client.cancelQueries('self');
      }

      client.invalidateQueries('self', {
        refetchInactive: true,
      });
    },
  });

  useErrorSnack(i18n.t('Kunde inte verifiera email'), isVerifyEmailError);
  useSuccessSnack(i18n.t('Email verifierad'), isVerifyEmailSuccess);

  const {
    mutate: _sendVerificationPhone,
    mutateAsync: sendVerificationPhoneAsync,
    isSuccess: isSendVerificationPhoneSuccess,
    error: isSendVerificationPhoneError,
    isLoading: isSendVerificationPhoneLoading,
  } = useMutation(sendVerificationPhone, {
    onSettled: () => {
      client.invalidateQueries('self');
    },
  });

  useErrorSnack(
    i18n.t('Kunde inte skicka verifierings-sms'),
    isSendVerificationPhoneError
  );
  useSuccessSnack(
    i18n.t('Verifierings-sms skickat'),
    isSendVerificationPhoneSuccess
  );

  const {
    mutate: _verifyPhone,
    mutateAsync: verifyPhoneAsync,
    isSuccess: isVerifyPhoneSuccess,
    error: isVerifyPhoneError,
    isLoading: isVerifyPhoneLoading,
  } = useMutation(verifyPhone, {
    onSettled: () => {
      client.invalidateQueries('self', {
        refetchInactive: true,
      });
    },
  });

  useErrorSnack(
    i18n.t('Kunde inte verifiera telefonnummer'),
    isVerifyPhoneError
  );
  useSuccessSnack(i18n.t('Telefonnummer verifierat'), isVerifyPhoneSuccess);

  const { mutate: _createWorkspace, mutateAsync: createWorkspaceAsync } =
    useMutation(createWorkspace, {
      onSettled: () => {
        client.invalidateQueries(); //invalidates whole cache
      },
    });

  const { mutateAsync: updateUserExportSettingsAsync } = useMutation(
    updateUserExportSettings
  );

  const { mutate: _createIntegration } = useMutation(createIntegration, {
    onSettled: () => {
      client.invalidateQueries('self');
    },
  });

  const { mutate: _deleteIntegration } = useMutation(deleteIntegration, {
    onSettled: () => {
      client.invalidateQueries('self');
    },
  });

  const {
    mutate: _removeCalendarConnection,
    mutateAsync: removeCalendarConnectionAsync,
    isSuccess: isRemoveCalendarConnectionSuccess,
    isError: isRemoveCalendarConnectionError,
  } = useMutation(removeCalendarConnection, {
    onSettled: () => {
      client.invalidateQueries('self');
    },
  });

  useSuccessSnack(
    i18n.t('Kalenderkoppling borttagen'),
    isRemoveCalendarConnectionSuccess
  );

  useErrorSnack(
    i18n.t('Kunde inte ta bort kalenderkoppling'),
    isRemoveCalendarConnectionError
  );

  const {
    mutate: _toggleCalendarSyncMeeting,
    mutateAsync: toggleCalendarSyncMeetingAsync,
    isError: isToggleCalendarSyncMeetingError,
  } = useMutation(toggleCalendarSyncMeeting, {
    onMutate: ({ calendarId }) => {
      client.setQueryData('upcoming-meetings', (old) => {
        const newUpcomingMeetings = (old.upcomingMeetings || []).map(
          (meeting) => {
            if (meeting.id === calendarId) {
              return { ...meeting, willJoin: !meeting.willJoin };
            }
            return meeting;
          }
        );

        return {
          upcomingMeetings: newUpcomingMeetings,
        };
      });

      return { calendarId };
    },
    onError: (err, variables, context) => {
      client.setQueryData('upcoming-meetings', (old) => {
        const newUpcomingMeetings = (old.upcomingMeetings || []).map(
          (meeting) => {
            if (meeting.id === context.calendarId) {
              return { ...meeting, willJoin: !meeting.willJoin };
            }
            return meeting;
          }
        );

        return {
          upcomingMeetings: newUpcomingMeetings,
        };
      });

      client.invalidateQueries('self');
      client.invalidateQueries('upcoming-meetings');
    },
    onSettled: () => {
      client.invalidateQueries('self');
      client.invalidateQueries('upcoming-meetings');
    },
  });

  useErrorSnack(
    i18n.t('Kunde inte ändra kalenderinställning'),
    isToggleCalendarSyncMeetingError
  );

  const {
    mutate: _toggleCalendarMeetingBotSpeakers,
    mutateAsync: toggleCalendarMeetingBotSpeakersAsync,
    isError: isCalendarMeetingBotSpeakersError,
  } = useMutation(toggleCalendarMeetingBotSpeakers, {
    onMutate: ({ calendarId }) => {
      client.setQueryData('upcoming-meetings', (old) => {
        const newUpcomingMeetings = (old.upcomingMeetings || []).map(
          (meeting) => {
            if (meeting.id === calendarId) {
              return {
                ...meeting,
                meetingBotSpeakers: !meeting.meetingBotSpeakers,
              };
            }
            return meeting;
          }
        );

        return {
          upcomingMeetings: newUpcomingMeetings,
        };
      });

      return { calendarId };
    },
    onError: (err, variables, context) => {
      client.setQueryData('upcoming-meetings', (old) => {
        const newUpcomingMeetings = (old.upcomingMeetings || []).map(
          (meeting) => {
            if (meeting.id === context.calendarId) {
              return {
                ...meeting,
                meetingBotSpeakers: !meeting.meetingBotSpeakers,
              };
            }
            return meeting;
          }
        );

        return {
          upcomingMeetings: newUpcomingMeetings,
        };
      });

      client.invalidateQueries('self');
      client.invalidateQueries('upcoming-meetings');
    },
    onSettled: () => {
      client.invalidateQueries('self');
      client.invalidateQueries('upcoming-meetings');
    },
  });

  useErrorSnack(
    i18n.t('Kunde inte ändra kalenderinställning'),
    isCalendarMeetingBotSpeakersError
  );

  const {
    mutate: _deleteSelf,
    mutateAsync: deleteSelfAsync,
    isSuccess: deleteSuccess,
    isError: deleteError,
  } = useMutation(() => deleteSelf(), {
    onSettled: () => {
      client.invalidateQueries(); //invalidates whole cache
    },
  });

  useSuccessSnack(i18n.t('Användare raderad'), deleteSuccess);
  useErrorSnack(i18n.t('Fel uppstod när användaren raderades'), deleteError);

  return {
    updateUser: update,
    updateUserAsync,
    isUpdateUserLoading: isLoading,
    isUpdateUserSuccess: isSuccess,
    isUpdateUserError: isError,
    switchWorkspace: _switchWorkspace,
    switchWorkspaceAsync,
    saveOnboarding: _saveOnboarding,
    saveOnboardingAsync: _saveOnboardingAsync,
    createOtp: _createOtp,
    createOtpAsync,
    isOtpSuccess,
    isOtpError,
    isOtpLoading,
    otp,
    verifyOtp: _verifyOtp,
    verifyOtpAsync,
    isVerifyOtpSuccess,
    isVerifyOtpError,
    verifyOtpError,
    deleteOtp: _deleteOtp,
    deleteOtpError,
    deleteOtpAsync,
    sendVerificationEmail: _sendVerificationEmail,
    sendVerificationEmailAsync,
    isSendVerificationEmailError,
    isSendVerificationEmailSuccess,
    isSendVerificationEmailLoading,
    verifyEmail: _verifyEmail,
    verifyEmailAsync,
    isVerifyEmailError,
    isVerifyEmailSuccess,
    isVerifyEmailLoading,
    resetVerifyEmail,
    resetSendVerificationEmail,
    resetDelete,
    resetVerifyOtp,
    createWorkspace: _createWorkspace,
    createWorkspaceAsync,
    updateUserExportSettingsAsync,
    createIntegration: _createIntegration,
    deleteIntegration: _deleteIntegration,
    sendVerificationPhone: _sendVerificationPhone,
    sendVerificationPhoneAsync,
    isSendVerificationPhoneError,
    isSendVerificationPhoneSuccess,
    isSendVerificationPhoneLoading,
    verifyPhone: _verifyPhone,
    verifyPhoneAsync,
    isVerifyPhoneError,
    isVerifyPhoneSuccess,
    isVerifyPhoneLoading,
    removeCalendarConnection: _removeCalendarConnection,
    removeCalendarConnectionAsync,
    toggleCalendarSyncMeeting: _toggleCalendarSyncMeeting,
    toggleCalendarSyncMeetingAsync,
    toggleCalendarMeetingBotSpeakers: _toggleCalendarMeetingBotSpeakers,
    toggleCalendarMeetingBotSpeakersAsync,
    deleteSelf: _deleteSelf,
    deleteSelfAsync,
  };
}

export function useUsersActions() {
  const {
    mutate: create,
    mutateAsync: createUserAsync,
    isError,
    isSuccess,
  } = useMutation(createUser);

  useSuccessSnack(i18n.t('Lyckades skapa användare'), isSuccess);
  useErrorSnack(i18n.t('Kunde inte skapa användare'), isError);

  const { mutate: _signInWithGoogle, mutateAsync: signInWithGoogleAsync } =
    useMutation(signInWithGoogle);

  return {
    createUser: create,
    createUserAsync,
    signInWithGoogle: _signInWithGoogle,
    signInWithGoogleAsync,
  };
}

export function useUserQuery({
  month,
  billing: useBilling = true,
  enabled = true,
} = {}) {
  const {
    data: userData,
    isLoading,
    isFetching,
  } = useQuery('self', () => getSelf(), {
    staleTime: 5 * 60 * 1000,
    enabled,
  });
  const user = useMemo(() => userData || {}, [userData]);

  if (!month) {
    month = new Date().getMonth();
  }

  return {
    user,
    isLoading,
    isFetching,
  };
}

export function useUsersQuery({ search, inOrg = false } = {}) {
  const { data } = useQuery(['users', search], () =>
    getUsers({ search, inOrg })
  );

  const users = useMemo(() => data || [], [data]);

  return {
    users,
  };
}

export function useAdminUserQuery({ userId, search }) {
  //Admin view of user. Can se billing and other info

  const { data: user } = useQuery(['user', userId], () => getUser(userId), {
    enabled: !!userId,
  });
  const { data: billingEvents } = useQuery(
    ['user_billing', userId],
    () => getUserBilling(userId),
    {
      enabled: !!userId,
    }
  );

  const { data } = useQuery(['users', search], () => getUsers({ search }));

  const users = useMemo(() => data || [], [data]);

  return {
    user,
    billingEvents,
    users,
  };
}

export function useAdminUserActions(userId) {
  const client = useQueryClient();

  const {
    mutate: _createNameList,
    mutateAsync: _createNameListAsync,
    isSuccess: createNameListSuccess,
    isError: createNameListError,
  } = useMutation((data) => createNameList(userId, data), {
    onSettled: () => {
      client.invalidateQueries(['user', userId]);
    },
  });

  useSuccessSnack(i18n.t('Namnlista skapades'), createNameListSuccess);
  useErrorSnack(
    i18n.t('Fel uppstod när namnlista skapades'),
    createNameListError
  );

  const {
    mutate: _createTranscribeMode,
    mutateAsync: _createTranscribeModeAsync,
    isSuccess: createModeSuccess,
    isError: createTranscribeModeError,
  } = useMutation((name) => createTranscribeMode(userId, name), {
    onSettled: () => {
      client.invalidateQueries(['user', userId]);
    },
  });
  useSuccessSnack(i18n.t('Transkriberingsmodul skapad'), createModeSuccess);
  useErrorSnack(
    i18n.t('Fel uppstod när transkriberingsmodulen skapades'),
    createTranscribeModeError
  );

  const {
    mutate: updateUser,
    isSuccess: updateSuccess,
    isError: updateError,
  } = useMutation((data) => updateUserAdmin(userId, data), {
    onSettled: (data, err) => {
      client.invalidateQueries(['user', userId]);
    },
  });
  useSuccessSnack(i18n.t('Updaterade användare'), updateSuccess);
  useErrorSnack(
    i18n.t('Fel uppstod vid updatering av användaren'),
    updateError
  );

  const {
    mutate: deleteUser,
    mutateAsync: deleteUserAsync,
    isSuccess: deleteSuccess,
    isError: deleteError,
  } = useMutation(() => deleteUserAdmin(userId), {
    onSettled: () => {
      client.invalidateQueries(['user', userId]);
    },
  });

  useSuccessSnack(i18n.t('Användare raderad'), deleteSuccess);
  useErrorSnack(i18n.t('Fel uppstod när användaren raderades'), deleteError);

  return {
    createNameList: _createNameList,
    createNameListAsync: _createNameListAsync,
    createTranscribeMode: _createTranscribeMode,
    createTranscribeModeAsync: _createTranscribeModeAsync,
    updateUser,
    deleteUser,
    deleteUserAsync,
  };
}

async function getInvite(code) {
  const res = await get(`/user/invite/${code}`);
  return res?.data;
}

export function useInviteUserQuery({ token }) {
  const { data, isLoading, isError } = useQuery(
    ['invite', token],
    () => getInvite(token),
    {
      enabled: !!token,
    }
  );

  return { invite: data, isLoading, isError };
}

export async function getMsCalendarAuthUrl() {
  const res = await get('/user/calendar-sync/ms-auth-url');
  return res?.data;
}

export async function getGoogleCalendarAuthUrl() {
  const res = await get('/user/calendar-sync/google-auth-url');
  return res?.data;
}

export async function resendEmailSecureLoginVerification() {
  const res = await post('/secure-login/resend-email');
  return res?.data;
}

async function getSecureLoginSelf(token) {
  const res = await get(`/secure-login/${token}/self`);
  return res?.data;
}

export function useSecureLoginUserQuery(token) {
  const { data, isLoading } = useQuery('secure-login', () =>
    getSecureLoginSelf(token)
  );

  return { ...data, isLoading };
}

async function createOtpSecureLogin(token) {
  const res = await post(`/secure-login/${token}/otp`);
  return res?.data;
}

async function verifyOtpSecureLogin({
  token,
  secret,
  backupCodes,
  loginToken,
}) {
  const res = await post('/secure-login/otp/verify', {
    token,
    secret,
    backup_codes: backupCodes,
    login_token: loginToken,
  });
  return res?.data;
}

export function useSecureLoginActions(token) {
  const client = useQueryClient();

  const {
    mutate: _createOtp,
    mutateAsync: createOtpAsync,
    isSuccess: isOtpSuccess,
    isError: isOtpError,
    loading: isOtpLoading,
    data: otp,
  } = useMutation(() => createOtpSecureLogin(token));

  const {
    mutate: _verifyOtp,
    mutateAsync: verifyOtpAsync,
    isSuccess: isVerifyOtpSuccess,
    isError: isVerifyOtpError,
    error: verifyOtpError,
    reset: resetVerifyOtp,
  } = useMutation(verifyOtpSecureLogin, {
    onSettled: () => {
      client.invalidateQueries('secure-login');
    },
  });

  return {
    createOtp: _createOtp,
    createOtpAsync,
    isOtpSuccess,
    isOtpError,
    isOtpLoading,
    otp,
    verifyOtp: _verifyOtp,
    verifyOtpAsync,
    isVerifyOtpSuccess,
    isVerifyOtpError,
    verifyOtpError,
    resetVerifyOtp,
  };
}

export async function getUserEdits() {
  const res = await get('/user/automatic-edits');
  return res?.data;
}

async function reportUserLastSeen() {
  try {
    const { secure_login: secureLogin, otp_token: otpToken, user_id: userId } =
      parseJwt(store[TOKEN].value) || {};

    if (secureLogin || otpToken || !userId) {
      return {};
    }

    await post('/user/last-seen');
  } catch (e) {
    // Ignore
  }
}

export async function useReportUserLastSeen() {
  const [token] = useStore(TOKEN);

  useEffect(() => {
    if (token) {
      reportUserLastSeen();
    }
  }, [token]);

  useInterval(
    async () => {
      if (token) {
        reportUserLastSeen();
      }
    },
    !!token ? 1000 * 60 * 5 : null
  );
}
